import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, Landmark, Sparkles } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { t, useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const TYPES = [
  ["", "All"],
  ["loan", "🏦 Loans"],
  ["scholarship", "🎓 Scholarships"],
  ["grant", "🚀 Grants & Investors"],
];

const inputCls = "w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60";

// FundHub — loans, scholarships, grants & investor options with a FUNZI advisor,
// one-tap applications and admin review.
export const FundHubPanel = () => {
  const { user } = useAuth();
  const lang = useLang();
  const isAdmin = user.role === "admin";
  const [tab, setTab] = useState("explore");
  const [options, setOptions] = useState(null);
  const [type, setType] = useState("");
  const [advisor, setAdvisor] = useState(null);
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [applyFor, setApplyFor] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => {
    const country = encodeURIComponent(user.localization?.country || "India");
    axios.get(`${API}/funding/options?type=${type}&country=${country}`, headers()).then((r) => setOptions(r.data.options)).catch(() => setOptions([]));
    axios.get(`${API}/funding/advisor`, headers()).then((r) => setAdvisor(r.data)).catch(() => setAdvisor(null));
    axios.get(`${API}/funding/mine`, headers()).then((r) => setMine(r.data.applications)).catch(() => {});
    if (isAdmin) axios.get(`${API}/funding/applications`, headers()).then((r) => setAll(r.data.applications)).catch(() => {});
  };
  useEffect(load, [type]);

  const apply = async () => {
    setBusy("apply");
    try {
      await axios.post(`${API}/funding/apply`, { option_id: applyFor.id, amount: Number(amount), note }, headers());
      toast.success("Application submitted — you'll be pinged on every update 💰");
      setApplyFor(null);
      setAmount("");
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const review = async (id, status) => {
    setBusy(id);
    try {
      await axios.post(`${API}/funding/applications/${id}/status`, { status, note: "" }, headers());
      toast.success(`Application ${status.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const tabs = [
    { id: "explore", label: `🏦 ${t(lang, "fh_explore")}` },
    { id: "mine", label: `📋 ${t(lang, "fh_mine")}` },
    ...(isAdmin ? [{ id: "review", label: `✅ ${t(lang, "fh_review")}` }] : []),
  ];

  const statusColor = (s) =>
    s === "approved" ? "bg-[#22C55E]/10 text-[#22C55E]" : s === "rejected" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-white/5 text-slate-400";

  return (
    <div className="space-y-5" data-testid="panel-fundhub">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`fh-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`rounded-full px-4 py-2 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === tb.id ? "bg-gradient-to-r from-[#22C55E] to-[#06B6D4] text-[#09090B]" : "border border-white/10 text-slate-400 hover:text-white"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "explore" && (
        <>
          {advisor && advisor.picks?.length > 0 && (
            <div className="rounded-2xl border border-[#06B6D4]/30 bg-[#06B6D4]/[0.06] p-4" data-testid="fh-advisor">
              <p className="flex items-center gap-1.5 font-jbmono text-[9px] uppercase tracking-widest text-[#06B6D4]">
                <Sparkles className="h-3 w-3" /> FUNZI advisor{advisor.source === "fallback" ? " · basic mode" : ""}
              </p>
              <div className="mt-2.5 space-y-2">
                {advisor.picks.map((p, i) => (
                  <p key={i} className="text-xs leading-relaxed text-slate-200" data-testid={`fh-pick-${i}`}>
                    <span className="font-bold text-white">{i + 1}. {p.name}</span> — {p.why}
                  </p>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {TYPES.map(([k, label]) => (
              <button key={k} data-testid={`fh-type-${k || "all"}`} onClick={() => setType(k)}
                className={`rounded-full px-4 py-2 text-[10px] font-bold ${type === k ? "bg-[#22C55E] text-[#09090B]" : "border border-white/10 text-slate-400"}`}>
                {label}
              </button>
            ))}
          </div>
          {options === null ? (
            <LoaderCircle className="h-5 w-5 animate-spin text-[#06B6D4]" />
          ) : options.length === 0 ? (
            <p className="text-sm text-slate-500">No options for this filter in your country yet.</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {options.map((o) => (
                <div key={o.id} data-testid={`fh-option-${o.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold">{o.name}</p>
                      <p className="font-jbmono text-[10px] text-slate-500">
                        {o.provider} · {o.type}{o.interest ? ` · ${o.interest}` : ""} · {o.country}
                      </p>
                    </div>
                    <span className="shrink-0 font-jbmono text-xs font-bold text-[#22C55E]">₹{o.max_amount.toLocaleString("en-IN")}</span>
                  </div>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-slate-400">{o.blurb}</p>
                  {applyFor?.id === o.id ? (
                    <div className="mt-3 space-y-2 rounded-xl border border-[#22C55E]/30 bg-[#22C55E]/[0.05] p-3">
                      <input data-testid="fh-amount" type="number" min="1000" max={o.max_amount} value={amount} onChange={(e) => setAmount(e.target.value)}
                        placeholder={`Amount (max ₹${o.max_amount.toLocaleString("en-IN")})`} className={inputCls} />
                      <input data-testid="fh-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What is it for? (e.g. final-year fees)" className={inputCls} />
                      <div className="flex gap-2">
                        <button data-testid="fh-apply-confirm" onClick={apply} disabled={busy === "apply" || !amount}
                          className="flex-1 rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] py-2.5 font-display text-xs font-bold uppercase text-[#09090B] disabled:opacity-50">
                          {busy === "apply" ? <LoaderCircle className="mx-auto h-3.5 w-3.5 animate-spin" /> : t(lang, "fh_apply")}
                        </button>
                        <button onClick={() => setApplyFor(null)} className="rounded-full border border-white/15 px-4 text-xs text-slate-400">{t(lang, "cancel")}</button>
                      </div>
                    </div>
                  ) : (
                    <button data-testid={`fh-apply-${o.id.slice(-6)}`} onClick={() => setApplyFor(o)}
                      className="mt-3 w-full rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B]">
                      {t(lang, "fh_apply")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {(tab === "mine" || tab === "review") && (
        <div className="space-y-2.5">
          {(tab === "mine" ? mine : all).length === 0 && (
            <p className="text-sm text-slate-500">{tab === "mine" ? "No applications yet — explore funding options." : "No applications to review."}</p>
          )}
          {(tab === "mine" ? mine : all).map((a) => (
            <div key={a.id} data-testid={`fh-app-${a.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold">
                    <Landmark className="mr-1.5 inline h-3.5 w-3.5 text-[#22C55E]" />
                    {a.option_name}
                    {tab === "review" && <span className="font-jbmono text-[10px] text-slate-500"> · {a.user_name} ({a.user_role})</span>}
                  </p>
                  <p className="font-jbmono text-[10px] text-slate-500">
                    {a.provider} · ₹{a.amount.toLocaleString("en-IN")} · {String(a.created_at).slice(0, 10)}{a.note ? ` · ${a.note}` : ""}
                  </p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${statusColor(a.status)}`}>
                  {a.status.replace("_", " ")}
                </span>
              </div>
              {a.admin_note && <p className="mt-1.5 font-jbmono text-[10px] text-[#06B6D4]">💬 {a.admin_note}</p>}
              {tab === "review" && ["submitted", "under_review"].includes(a.status) && (
                <div className="mt-2.5 flex gap-2">
                  {a.status === "submitted" && (
                    <button data-testid={`fh-review-${a.id.slice(-6)}`} onClick={() => review(a.id, "under_review")}
                      className="rounded-full border border-white/15 px-4 py-1.5 text-[10px] text-slate-300 hover:text-white">Mark under review</button>
                  )}
                  <button data-testid={`fh-approve-${a.id.slice(-6)}`} onClick={() => review(a.id, "approved")}
                    className="rounded-full bg-[#22C55E] px-4 py-1.5 text-[10px] font-bold text-[#09090B]">Approve</button>
                  <button data-testid={`fh-reject-${a.id.slice(-6)}`} onClick={() => review(a.id, "rejected")}
                    className="rounded-full border border-[#F97316]/40 px-4 py-1.5 text-[10px] text-[#F97316]">Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
